import { useAskInputState } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { useRefreshBoxPickerCredentials } from '#pplx/frontend/apps/ask/hooks/connectors-hooks/box/useRefreshBoxPickerCredentials.js';
import { useMicrosoftPickerModal } from '#pplx/frontend/apps/ask/hooks/connectors-hooks/microsoft/useMicrosoftPickerModal.js';
import { useAttachmentTokenEstimationParams } from '#pplx/frontend/apps/ask/hooks/eppo-hooks/useAttachmentTokenEstimationParams.js';
import { useFileUploadRateLimitPaywall } from '#pplx/frontend/apps/ask/hooks/file-attachments-hooks/useDefaultUploadGate.js';
import { useMediaFileUrlIngress, useMediaImageUrlResolver, } from '#pplx/frontend/apps/ask/hooks/file-attachments-hooks/useMediaFileUrlIngress.js';
import { getUserSettingsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/userKeys.js';
import { getUserSettings } from '#pplx/frontend/apps/ask/queries/user-queries/getUserSettings.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { useFileUpload } from '#pplx/frontend/components/hooks/useFileUpload.js';
import { getInputFocusState } from '#pplx/frontend/composer-sdk/lexical/utils/inputFocusState.js';
import { useCallback, useImperativeHandle, useMemo, useRef } from 'react';
export const useFileAttachments = ({ fileUploadRef, isAudioVideoFilesEnabled, isFollowUp, lastResult, onStartFileUpload, onCompleteFileUpload, setUploadsReady, reason, specialCapabilities, askInputRef, editorFocusRef, onRemoveFile, uploadPolicy, gateUploadFile, prepareFiles, additionalFileCount = 0, }) => {
    const fileInputRef = useRef(null);
    const { inputRef } = useAskInputState();
    const attachmentTokenEstimationParams = useAttachmentTokenEstimationParams();
    const isUserLoggedIn = useIsUserLoggedIn();
    const handleOpenPricingModal = useFileUploadRateLimitPaywall();
    const { onOpen: onOpenMicrosoft, onClose: onCloseMicrosoft, onError: onErrorMicrosoft, toPickerDocument, options: microsoftFilePickerOptions, onSelectSite: handleSelectSharepointSite, isOpen: isMicrosoftFilePickerOpen, } = useMicrosoftPickerModal({ mode: 'attachment' });
    const { mutateAsync: refreshBoxCredentials } = useRefreshBoxPickerCredentials({ reason });
    const refreshBoxCredentialsForConnection = useCallback((connectionUuid) => refreshBoxCredentials({ connectionUuid }), [refreshBoxCredentials]);
    const getImageUrl = useMediaImageUrlResolver({
        backendUUID: lastResult?.backend_uuid,
        reason,
    });
    const fileUpload = useFileUpload({
        analyticsSurface: 'thread_attachment',
        fileInputRef,
        isSignedIn: isUserLoggedIn,
        uploadRateLimit: uploadPolicy.quota.remainingUploadCount,
        maxFilesUpload: uploadPolicy.constraints.maxFiles,
        attachmentTokenEstimationParams,
        onStart: onStartFileUpload,
        onComplete: onCompleteFileUpload,
        gateUploadFile,
        openPricingModal: handleOpenPricingModal,
        openMicrosoftPicker: onOpenMicrosoft,
        closeMicrosoftPicker: onCloseMicrosoft,
        errorMicrosoftPicker: onErrorMicrosoft,
        // Ask web flows let Google decide whether to prompt for re-consent; the
        // Outlook add-in keeps the silent session-reuse default.
        googlePromptBehavior: 'google_decides',
        toPickerDocument,
        setUploadRateLimit: uploadPolicy.quota.consumeUploadCount,
        isFollowUp,
        refreshBoxCredentials: refreshBoxCredentialsForConnection,
        setUploadsReady,
        getImageUrl,
        isAudioVideoFilesEnabled,
        reason,
        maxAttachmentFileSizeMB: uploadPolicy.constraints.maxFileSizeMB,
        specialCapabilities,
        onRemoveFile,
        getUserSettingsQueryKey,
        getUserSettings,
        skipParsing: uploadPolicy.transport.skipParsing,
        defaultFileSource: uploadPolicy.transport.defaultFileSource,
    });
    // expose imperatives to parent through AskInput ref if provided
    const { addAttachmentUrl, handleClearFiles, handleFileInput: handleUnpreparedFileInput, handleFileInputAndWaitForRegistration: handleUnpreparedFileInputAndWaitForRegistration, removeAllFiles, } = fileUpload;
    const handleFileInput = useCallback((files, source, fileSource, metadata, selectionFileCount, onFilesRegistered, onFilesRegistrationRolledBack) => {
        const upload = (preparedFiles) => handleUnpreparedFileInput(preparedFiles, source, fileSource, metadata, additionalFileCount > 0
            ? (selectionFileCount ?? files?.length ?? 0) + additionalFileCount
            : selectionFileCount, onFilesRegistered, onFilesRegistrationRolledBack);
        if (!files || !prepareFiles) {
            upload(files);
            return;
        }
        void prepareFiles(files).then(upload, () => upload([]));
    }, [additionalFileCount, handleUnpreparedFileInput, prepareFiles]);
    const addLocalFile = useCallback((file, metadata) => {
        handleFileInput([file], 'local', undefined, metadata);
    }, [handleFileInput]);
    const addRestoredMediaFile = useCallback((file, { metadata }) => {
        // URL-restored media previously entered through useFileUpload's unwrapped
        // path, so it must not inherit local-selection preparation or file counts.
        handleUnpreparedFileInput([file], 'local', undefined, metadata);
    }, [handleUnpreparedFileInput]);
    const addMediaFile = useMediaFileUrlIngress({
        addFile: addRestoredMediaFile,
        getImageUrl,
    });
    const addMediaFileToAskInput = useCallback((url, filename, mimeType, metadata) => addMediaFile({ filename, metadata, mimeType, url }), [addMediaFile]);
    const handleFileInputAndWaitForRegistration = useCallback((files, source, fileSource, metadata) => {
        const upload = (preparedFiles) => handleUnpreparedFileInputAndWaitForRegistration(preparedFiles, source, fileSource, metadata);
        if (!files || !prepareFiles) {
            return upload(files);
        }
        return prepareFiles(files).then(upload, () => upload([]));
    }, [handleUnpreparedFileInputAndWaitForRegistration, prepareFiles]);
    useImperativeHandle(fileUploadRef, () => ({
        uploadFiles: (files, selectionFileCount) => handleFileInput(Array.from(files), undefined, undefined, undefined, selectionFileCount),
        uploadFilesAndWaitForRegistration: (files) => handleFileInputAndWaitForRegistration(Array.from(files)),
        clearFiles: handleClearFiles,
    }), [handleClearFiles, handleFileInput, handleFileInputAndWaitForRegistration]);
    useImperativeHandle(askInputRef ?? null, () => ({
        addFile: addLocalFile,
        addMediaFile: addMediaFileToAskInput,
        addAttachmentUrl: (url, filename, metadata) => {
            addAttachmentUrl(url, filename, metadata);
        },
        getFocusState: () => {
            if (editorFocusRef != null) {
                return editorFocusRef.current?.getFocusState() ?? getInputFocusState(null);
            }
            return getInputFocusState(inputRef.current?.element() ?? null);
        },
        focusInput: () => {
            inputRef.current?.focus();
        },
        blurInput: () => {
            inputRef.current?.blur();
        },
        removeAttachments: () => {
            handleClearFiles();
        },
    }), [
        addAttachmentUrl,
        addLocalFile,
        addMediaFileToAskInput,
        editorFocusRef,
        handleClearFiles,
        inputRef,
    ]);
    return useMemo(() => ({
        ...fileUpload,
        handleFileInput,
        handleFileInputAndWaitForRegistration,
        fileInputRef,
        microsoftFilePickerOptions,
        handleSelectSharepointSite,
        isMicrosoftFilePickerOpen,
        onCloseMicrosoft,
        addMediaFile,
        addAttachmentUrl,
        removeAllFiles,
    }), [
        fileUpload,
        handleFileInput,
        handleFileInputAndWaitForRegistration,
        handleSelectSharepointSite,
        isMicrosoftFilePickerOpen,
        microsoftFilePickerOptions,
        onCloseMicrosoft,
        addMediaFile,
        addAttachmentUrl,
        removeAllFiles,
    ]);
};
//# sourceMappingURL=useFileAttachments.js.map